import { useEffect, useMemo, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import {
  isConfigurationChatEnabled,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useSelectedEcommerceIds } from '@/hooks/use-selected-ecommerce-ids'
import { AIChatLauncher } from './ai-chat-launcher'
import { ChatPanel } from './chat-panel'
import { useChatContext } from './chat-context'
import { useAssistantTracking } from './use-assistant-tracking'
import { DOCK_MEDIA_QUERY } from './use-dock-width'

// Placeholder id 0 until the Flagr flag exists (TECH-4431): stay fully dark and
// make zero Flagr calls by not mounting the evaluator until the id is real.
export function AIChat() {
  if (isConfigurationChatEnabled <= 0) return null
  return <AIChatMounted />
}

function AIChatMounted() {
  const ecommerceId = useAppSelector(
    (s) => s.settingsApi.settings.response.ecommerce_id
  )
  // Send ecommerce_id (snake) so a single Flagr constraint gates the front and
  // the analytics-api check identically — the backend evaluates with it too.
  const flagContext = useMemo(
    () => ({ ecommerce_id: ecommerceId }),
    [ecommerceId]
  )
  const { isFlagEnabled } = useFeatureFlag(
    isConfigurationChatEnabled,
    flagContext
  )
  const role = useAppSelector((s) => s.userApi.getMe.response.user?.role)
  const isAdmin = role === Roles.ReadWrite
  // The chat answers for one store, so a view spanning several would get one store's answers.
  const singleStore = useSingleStoreSelected()
  const available = isFlagEnabled && isAdmin && singleStore

  const { activeContext, isOpen, openChat, closeChat, setChatAvailable } =
    useChatContext()
  const isDocked = useMediaQuery(DOCK_MEDIA_QUERY)
  const { trackOpened } = useAssistantTracking()
  const launcherRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(false)

  // Publish availability so screens can show or hide their own chat entry points.
  useEffect(() => {
    setChatAvailable(available)
  }, [available, setChatAvailable])

  // Once per open, not per render: `trackOpened` changes identity when the user
  // navigates between screens with the panel still open.
  const openTracked = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      openTracked.current = false
      return
    }
    if (openTracked.current) return
    openTracked.current = true
    trackOpened()
  }, [isOpen, trackOpened])

  useEffect(() => {
    if (!isOpen) {
      // Restore focus to the launcher when the panel closes.
      if (wasOpen.current) launcherRef.current?.focus()
      wasOpen.current = false
      return
    }
    wasOpen.current = true
    // Only the overlay is modal; the docked panel closes on its own Escape so it
    // does not steal the key from the page still visible beside it.
    if (isDocked) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeChat()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, isDocked, closeChat])

  if (!available) return null
  // No launcher on screens the assistant does not support: floating over their
  // controls only to report itself unavailable is worse than no entry point.
  if (!activeContext && !isOpen) return null

  return (
    <>
      <AIChatLauncher
        ref={launcherRef}
        onOpen={openChat}
        isHidden={isDocked && isOpen}
      />
      <AnimatePresence>
        {isOpen && <ChatPanel onClose={closeChat} docked={isDocked} />}
      </AnimatePresence>
    </>
  )
}

function useSingleStoreSelected() {
  const userEcommerces = useAppSelector(
    (s) => s.userApi.getMe.response.user?.e_commerces_ids
  )
  const selectedIds = useSelectedEcommerceIds()
  return (selectedIds ?? userEcommerces)?.length === 1
}
